/**
 * Editor modal de NOTAMs (la hoja oficial) — Fase 10a.3.
 *
 * Reutilizable para CREAR y EDITAR un párrafo. Solo admin+ debe poder
 * abrir este editor — el caller en `mount.ts` chequea el rol antes
 * de invocarlo.
 *
 *   const result = await openNotamEditor({
 *     mode: 'edit',
 *     initial: { body: '...', archived: false },
 *   });
 *   if (result?.action === 'save') { await api.updateNotam(id, result.payload); }
 *   if (result?.action === 'delete') { await api.deleteNotam(id); }
 *
 * Decisiones:
 *  - El modal cubre crear y editar con el mismo formulario para no
 *    duplicar markup. El botón Borrar solo aparece en modo `edit`.
 *  - El checkbox "Archivado" se conecta al campo `archived` del payload.
 *    Permite archivar sin borrar (decisión Fase 10 — los caducados se
 *    filtran en runtime, el archivo manual marca con `archived=true`).
 */

import type { NotamPayload } from '@51esc/shared';

import './notam-editor.css';

export type NotamEditorMode = 'create' | 'edit';

export interface NotamEditorOptions {
  mode: NotamEditorMode;
  /** Cuerpo y archivado iniciales al editar. */
  initial?: { body?: string; archived?: boolean };
}

export type NotamEditorResult =
  | { action: 'save'; payload: NotamPayload }
  | { action: 'delete' }
  | null;

const MAX_BODY = 2000;

interface ModalRefs {
  dialog: HTMLDialogElement;
  title: HTMLElement;
  bodyTa: HTMLTextAreaElement;
  bodyCount: HTMLElement;
  archivedChk: HTMLInputElement;
  saveBtn: HTMLButtonElement;
  deleteBtn: HTMLButtonElement;
  cancelBtn: HTMLButtonElement;
  errorBox: HTMLElement;
}

let refs: ModalRefs | null = null;

export function openNotamEditor(
  opts: NotamEditorOptions,
): Promise<NotamEditorResult> {
  const r = ensureModal();

  r.title.textContent =
    opts.mode === 'create' ? 'NUEVA NOTAM' : 'EDITAR NOTAM';
  r.saveBtn.textContent = opts.mode === 'create' ? 'Publicar' : 'Guardar';
  // Borrar solo en modo edit — al crear no hay nada que borrar.
  // El [hidden] necesita override CSS contra el `display: flex` del
  // .ne-field (UA stylesheet pierde por origen author).
  r.deleteBtn.hidden = opts.mode === 'create';

  const initialBody = opts.initial?.body ?? '';
  r.bodyTa.value = initialBody;
  updateCount(r, initialBody);
  r.archivedChk.checked = opts.initial?.archived ?? false;

  r.errorBox.hidden = true;
  r.errorBox.textContent = '';

  return new Promise<NotamEditorResult>((resolve) => {
    let resolved = false;
    const finish = (value: NotamEditorResult): void => {
      if (resolved) return;
      resolved = true;
      r.dialog.close();
      r.saveBtn.removeEventListener('click', onSave);
      r.deleteBtn.removeEventListener('click', onDelete);
      r.cancelBtn.removeEventListener('click', onCancel);
      r.dialog.removeEventListener('cancel', onCancel);
      r.dialog.removeEventListener('click', onBackdrop);
      r.bodyTa.removeEventListener('input', onBodyInput);
      resolve(value);
    };
    const onCancel = (ev?: Event): void => {
      ev?.preventDefault();
      finish(null);
    };
    const onBackdrop = (ev: MouseEvent): void => {
      if (ev.target === r.dialog) finish(null);
    };
    const onBodyInput = (): void => updateCount(r, r.bodyTa.value);
    const onSave = (): void => {
      const body = r.bodyTa.value.trim();
      if (body.length === 0) {
        showError(r, 'El texto es obligatorio.');
        return;
      }
      if (body.length > MAX_BODY) {
        showError(r, `Máximo ${MAX_BODY} caracteres.`);
        return;
      }
      // Siempre enviar `archived` — el backend acepta archivar en POST
      // y en PATCH. Permite crear directamente archivados (drafts) sin
      // mostrarlos en el tablón.
      finish({
        action: 'save',
        payload: { body, archived: r.archivedChk.checked },
      });
    };
    const onDelete = (): void => finish({ action: 'delete' });

    r.saveBtn.addEventListener('click', onSave);
    r.deleteBtn.addEventListener('click', onDelete);
    r.cancelBtn.addEventListener('click', onCancel);
    r.dialog.addEventListener('cancel', onCancel);
    r.dialog.addEventListener('click', onBackdrop);
    r.bodyTa.addEventListener('input', onBodyInput);

    r.dialog.showModal();
    r.bodyTa.focus();
  });
}

// ─── Helpers ──────────────────────────────────────────────────────────

function ensureModal(): ModalRefs {
  if (refs !== null) return refs;
  const dialog = document.createElement('dialog');
  dialog.className = 'ne-dialog';
  dialog.innerHTML = `
    <form class="ne-card" method="dialog" novalidate>
      <h2 class="ne-title"></h2>

      <label class="ne-field">
        <span class="ne-label">Texto del NOTAM</span>
        <textarea class="ne-textarea" rows="8" maxlength="2000" required></textarea>
        <small class="ne-count" aria-live="polite">0 / ${MAX_BODY}</small>
      </label>

      <label class="ne-field ne-archived-row">
        <input type="checkbox" class="ne-archived" />
        <span>Archivado (oculto del tablón hasta restaurar)</span>
      </label>

      <p class="ne-error" role="alert" hidden></p>

      <div class="ne-actions">
        <button type="button" class="ne-btn ne-delete">Borrar</button>
        <span class="ne-actions-spacer"></span>
        <button type="button" class="ne-btn ne-cancel">Cancelar</button>
        <button type="button" class="ne-btn ne-save"></button>
      </div>
    </form>
  `;
  document.body.appendChild(dialog);

  const get = <T extends HTMLElement>(sel: string): T => {
    const el = dialog.querySelector<T>(sel);
    if (!el) throw new Error(`[notam-editor] missing ${sel}`);
    return el;
  };
  refs = {
    dialog,
    title: get<HTMLElement>('.ne-title'),
    bodyTa: get<HTMLTextAreaElement>('.ne-textarea'),
    bodyCount: get<HTMLElement>('.ne-count'),
    archivedChk: get<HTMLInputElement>('.ne-archived'),
    saveBtn: get<HTMLButtonElement>('.ne-save'),
    deleteBtn: get<HTMLButtonElement>('.ne-delete'),
    cancelBtn: get<HTMLButtonElement>('.ne-cancel'),
    errorBox: get<HTMLElement>('.ne-error'),
  };
  return refs;
}

function updateCount(r: ModalRefs, value: string): void {
  r.bodyCount.textContent = `${value.length} / ${MAX_BODY}`;
}

function showError(r: ModalRefs, msg: string): void {
  r.errorBox.textContent = msg;
  r.errorBox.hidden = false;
}
